import { CloseButton } from '@components/shared/Dialog/CloseButton';
import { useClients } from '@features/clients';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { ExportCampaignRow } from './columns';
import { ExportCampaignDataGrid } from './ExportCampaignDataGrid';
import { ExportFilters } from './ExportFilters';
import type { ExportFilterState } from './filter-state';
import { exportFilterInitialState } from './filter-state';

type CrossChannelExportModalProps = {
  onClose: () => void;
  open: boolean;
};

export const CrossChannelExportModal = ({
  onClose: handleClose,
  open,
}: CrossChannelExportModalProps) => {
  const { clients, isLoading } = useClients();
  const [filterState, setFilterState] = useState<ExportFilterState>(
    exportFilterInitialState
  );
  const [selectedRows, setSelectedRows] = useState<ExportCampaignRow[]>([]);

  const effectiveClient = filterState.selectedClient ?? clients[0] ?? null;
  const effectiveAdvertiser =
    filterState.selectedAdvertiser ?? effectiveClient?.advertisers[0] ?? null;
  const resolvedFilterState: ExportFilterState = {
    ...filterState,
    selectedClient: effectiveClient,
    selectedAdvertiser: effectiveAdvertiser,
  };

  const handleFilterChange = (newFilterState: ExportFilterState) => {
    setFilterState(newFilterState);
    setSelectedRows([]);
  };

  const handleCloseModal = () => {
    setSelectedRows([]);
    handleClose();
  };

  return (
    <Dialog fullWidth maxWidth="xl" onClose={handleCloseModal} open={open}>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        Cross-Channel Analysis Export
        <CloseButton onClick={handleCloseModal} />
      </DialogTitle>
      <DialogContent>
        <Alert severity="info">
          <Typography variant="body2">
            Select 2 to 5 campaigns with overlapping flight dates for your
            analysis. Your analysis will be delivered as a CSV file via email.
          </Typography>
        </Alert>
        <ExportFilters
          clients={clients}
          filterState={resolvedFilterState}
          isLoading={isLoading}
          onFilterChange={handleFilterChange}
        />
        <ExportCampaignDataGrid
          filterState={resolvedFilterState}
          onSelectionChange={setSelectedRows}
          selectedRows={selectedRows}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', padding: 2 }}>
        <Button disabled={selectedRows.length < 2} variant="contained">
          Export to Email
        </Button>
      </DialogActions>
    </Dialog>
  );
};
